import React, { useState, createContext, useCallback, useRef } from 'react';
import { DEFAULT_COPILOT } from '../constants/defaultCopilot';

const context = {};

export const TemporaryChatContext = createContext(context);

export const TemporaryChatContextProvider = props => {
  const [selectedAgent, setSelectedAgent] = useState(() => {
    const saved = localStorage.getItem('agent');
    return saved ? JSON.parse(saved) : { ...DEFAULT_COPILOT }; // fallback
  });

  const [chatMessages, setChatmessages] = useState([]);
  const [workflowChatMessages, setWorkflowChatmessages] = useState([]);
  const [toolImages, setToolImages] = useState([]);
  const [chatLoaderMessages, setChatLoaderMessages] = useState({});
  const [webSearch, setWebSearch] = useState(false);
  const [tempText, setTempText] = useState('');
  const [sessionID, setSessionID] = useState(null);
  const [sessionAgent, setSessionAgent] = useState({ ...DEFAULT_COPILOT });
  const isActiveRef = useRef(true);

  const toggleRef = value => {
    isActiveRef.current = value;
  };

  const fakeAiLoader = param => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev; // do nothing if no user message exists

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isLoading: true, isStreaming: true };
      return [...prev.slice(0, -1), updatedLast];
    });
  };
  const fakeAiLoaderClear = param => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev; // do nothing if no user message exists

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isLoading: false, isStreaming: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  };
  const fakeWorkflowAiLoader = () => {
    setWorkflowChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, isLoading: true, isStreaming: true }];
    });
  };
  const fakeWorkflowAiLoaderClear = () => {
    setWorkflowChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, isLoading: false, isStreaming: false }];
    });
  };

  const updateUserChat = message => {
    const userMessage = { human: message, ai: '', source: [] };
    setChatmessages(prev => [...prev, userMessage]);
  };

  const updateBotChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev; // do nothing if no user message exists

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, ai: (last.ai || '') + newChunk, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateWorkflowUserChat = message => {
    const userMessage = { human: message, ai: '', source: [] };
    setWorkflowChatmessages(prev => [...prev, userMessage]);
  };

  const updateWorkflowBotChat = useCallback(newChunk => {
    setWorkflowChatmessages(prev => {
      if (prev.length === 0) return prev; // do nothing if no user message exists

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, ai: (last.ai || '') + newChunk, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateToolImages = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev; // do nothing if no user message exists

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, images: newChunk?.search_results, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateChatLoaderMessage = useCallback(newChunk => {
    setChatLoaderMessages(prev => {
      const updatedLast = { newChunk, isLoading: false };
      return updatedLast;
    });
  }, []);

  const updateChatSource = newSources => {
    setChatmessages(prev => {
      const last = prev[prev.length - 1];

      const websearchSources = newSources.filter(src => src.type === 'google_search');
      const fileSources = newSources.filter(src => src.type === 'file');

      const updatedLast = {
        ...last,
        source: websearchSources.length > 0 ? websearchSources : [],
        files: fileSources.length > 0 ? fileSources : [],
      };
      return [...prev.slice(0, -1), updatedLast];
    });
  };

  const updateWebSearch = () => {
    setWebSearch(!webSearch);
  };

  const updateAgent = agent => {
    localStorage.setItem('agent', JSON.stringify(agent));
    setSelectedAgent(agent);
  };

  const updateStepsChat = useCallback(data => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const step = {
        tool_name: data?.tool_name,
        tool_args: data?.tool_args,
        content: data?.content,
        isLoading: true,
        status: 'running',
      };
      const steps = [...(last.intermediate_steps || []), step];
      return [...prev.slice(0, -1), { ...last, intermediate_steps: steps }];
    });
  }, []);

  const updateStepsResultsChat = useCallback(data => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const steps = [...(last.intermediate_steps || [])];
      if (steps.length) {
        const i = steps.length - 1;
        steps[i] = { ...steps[i], tool_result: data?.tool_result, isLoading: false, status: 'done' };
      }
      return [...prev.slice(0, -1), { ...last, intermediate_steps: steps }];
    });
  }, []);

  const updateAllStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const steps = (last.intermediate_steps || []).map(step => ({ ...step, isLoading: false }));
      return [...prev.slice(0, -1), { ...last, intermediate_steps: steps }];
    });
  }, []);

  const finishStream = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, isLoading: false, isStreaming: false }];
    });
  }, []);

  const updateThinkingStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, isLoading: true }];
    });
  }, []);

  const updateDelegateBotChat = useCallback(member_id => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, delegate_agent: member_id }];
    });
  }, []);

  return (
    <TemporaryChatContext.Provider
      value={{
        chatMessages,
        workflowChatMessages,
        chatLoaderMessages,
        updateWorkflowBotChat,
        updateWorkflowUserChat,
        setSessionID,
        sessionID,
        setChatmessages,
        toolImages,
        setToolImages,
        updateToolImages,
        updateUserChat,
        updateBotChat,
        updateChatLoaderMessage,
        updateChatSource,
        fakeAiLoader,
        fakeAiLoaderClear,
        fakeWorkflowAiLoader,
        fakeWorkflowAiLoaderClear,
        updateStepsChat,
        updateStepsResultsChat,
        updateAllStepsLoading,
        finishStream,
        updateThinkingStepsLoading,
        updateDelegateBotChat,
        webSearch,
        updateWebSearch,
        setTempText,
        tempText,
        selectedAgent,
        setSelectedAgent,
        updateAgent,
        setSessionAgent,
        sessionAgent,
        toggleRef,
        isActiveRef,
      }}>
      {props.children}
    </TemporaryChatContext.Provider>
  );
};
